import { Injectable } from '@angular/core';
import { forkJoin, Observable } from 'rxjs';
import { mapTo, take, tap, map } from 'rxjs';
import { FormFieldValidator, FormFieldDefinition, FormDefinition  } from '@feature/_app_modules/dynamic-forms/dynamic-form';
import { WidgetConfiguration, WidgetConfigurationsService } from '@feature/widget-configs';
import { WidgetAdminHandler } from '@widget-admin/services/widget-admin-handler';
import { getDefaultWidgetConfigAdminForm } from '@feature/widget-admin/services/widget-admin-form';
import { mapWherePropertiesMatch } from '@common/index';
import { MojoFormsInvoiceService } from '@feature/invoice-upload/services/mojo-forms-invoice.service';
import { InvoiceDocumentService } from '@feature/invoice-upload/services/invoice-document.service';
import { MojoFormsDesign } from '@feature/invoice-upload/models/mojo-forms-design';
import { DocumentType } from '@feature/invoice-upload/models/document-type-code';
import { SaveInvoiceUploadWidgetConfigurationRequest } from
  '@feature/invoice-upload/models/save-invoice-upload-widget-configuration-request';

@Injectable({
  providedIn: 'root'
})
export class InvoiceUploadWidgetAdminHandler implements WidgetAdminHandler {

  private invoiceFormDesigns: MojoFormsDesign[] = [];
  private documentTypes: DocumentType[] = [];

  constructor(
    private readonly widgetConfigService: WidgetConfigurationsService,
    private readonly invoiceService: InvoiceDocumentService,
    private readonly formsService: MojoFormsInvoiceService) { }

  initialise(): boolean | Observable<boolean> {

    const getFormDesigns = this.formsService.getInvoiceFormsDesigns().pipe(
      tap(designs => this.invoiceFormDesigns = designs || []),
      take(1)
    );

    const getDocumentTypes = this.invoiceService.getDocumentTypes().pipe(
      tap((docTypes) => this.documentTypes = docTypes || []),
      take(1)
    );


    const all = forkJoin([
      getFormDesigns,
      getDocumentTypes
    ]).pipe(mapTo(true as boolean));

    return all;
  }

  private createSaveRequest(selectedConfig: WidgetConfiguration, adminFormValue: any): SaveInvoiceUploadWidgetConfigurationRequest {

    const copyOfSelectedConfig: WidgetConfiguration = mapWherePropertiesMatch(selectedConfig, adminFormValue);
    const saveRequest = {} as SaveInvoiceUploadWidgetConfigurationRequest;

    saveRequest.widgetConfiguration = copyOfSelectedConfig;
    saveRequest.invoiceUploadWidgetConfigurationModel = {
      FormDesignId: adminFormValue.associatedMojoForm.Key,
      InvoiceTransactionTypeCode: adminFormValue.invoiceTransactionType.Code,
      CreditNoteTransactionTypeCode: adminFormValue.creditNoteTransactionType.Code
    };

    return saveRequest;
  }

  getAdminForm(selectedConfig: WidgetConfiguration): FormDefinition | Observable<FormDefinition> {

    const getFormFromConfig = this.invoiceService.getInvoiceUploadConfiguration(selectedConfig.id)
      .pipe(map(invoiceConfig => {

        const getDocType = (code) => this.documentTypes.find(doc => doc.Code === code);

        const formDefinition = getDefaultWidgetConfigAdminForm(selectedConfig);
        const defaultInvoiceTransactionType = invoiceConfig ? getDocType(invoiceConfig.InvoiceTransactionTypeCode) : getDocType('PIN');
        const defaultCreditNoteTransactionType = invoiceConfig ? getDocType(invoiceConfig.CreditNoteTransactionTypeCode) : getDocType('PCN');
        const defaultFormDesign = invoiceConfig ? this.invoiceFormDesigns.find(fd => fd.Key === invoiceConfig.FormDesignId) : this.invoiceFormDesigns.find(fd => fd.IsDefault);


        formDefinition.fields.push(
          {
            key: 'associatedMojoForm',
            label: 'Associated Mojo Form',
            value: defaultFormDesign,
            type: 'dropdown',
            dataKey: 'Key',
            optionLabel: 'Value',
            options: this.invoiceFormDesigns,
            validation: [
              { key: 'required', value: true, message: 'The Associated Mojo Form is required' } as FormFieldValidator
            ],
            asyncValidation: [],
          } as FormFieldDefinition
        );

        formDefinition.fields.push(
          {
            key: 'invoiceTransactionType',
            label: 'Invoice Transaction Type',
            value: defaultInvoiceTransactionType,
            type: 'dropdown',
            dataKey: 'Code',
            optionLabel: 'Description',
            options: this.documentTypes,
            validation: [{ key: 'required', value: true, message: 'Invoice Transaction Type' } as FormFieldValidator],
            asyncValidation: [],
          } as FormFieldDefinition
        );

        formDefinition.fields.push(
          {
            key: 'creditNoteTransactionType',
            label: 'Credit Note Transaction Type',
            value: defaultCreditNoteTransactionType,
            type: 'dropdown',
            dataKey: 'Code',
            optionLabel: 'Description',
            options: this.documentTypes,
            validation: [
              { key: 'required', value: true, message: 'Credit Note Transaction Type' } as FormFieldValidator
            ],
            asyncValidation: [],
          } as FormFieldDefinition
        );

        return formDefinition;

      }));

    return getFormFromConfig;
  }

  createCopyFromAdminForm(selectedConfig: WidgetConfiguration, adminFormValue): Observable<boolean> {
    const saveRequest = this.createSaveRequest(selectedConfig, adminFormValue);
    return this.invoiceService.createCopyOfInvoiceUploadConfiguration(saveRequest);
  }

  saveConfigFromAdminForm(selectedConfig: WidgetConfiguration, adminFormValue): Observable<boolean> {
    const saveRequest = this.createSaveRequest(selectedConfig, adminFormValue);
    return this.invoiceService.saveInvoiceUploadConfiguration(saveRequest).pipe(map(s => true));
  }

  deleteConfigFromAdminForm(selectedConfig: WidgetConfiguration, adminFormValue): Observable<boolean> {
    const copyOfSelectedConfig: WidgetConfiguration = mapWherePropertiesMatch(selectedConfig, adminFormValue);
    return this.widgetConfigService.delete(copyOfSelectedConfig).pipe(map(() => true));
  }

}
